"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { 
  QuestionMarkCircleIcon, 
  CpuChipIcon, 
  MagnifyingGlassIcon, 
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  UserGroupIcon,
  CogIcon, 
  ChatBubbleBottomCenterTextIcon, 
  CheckCircleIcon, 
  RocketLaunchIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

export default function HowItWorks() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: isMobile ? -20 : -50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const steps = [
    {
      icon: QuestionMarkCircleIcon,
      title: "L'utilisateur pose une question RH",
      description: "Votre collaborateur pose sa question directement dans ConnectRH, en langage naturel.",
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      hoverBorder: "hover:border-purple-400"
    },
    {
      icon: CpuChipIcon,
      title: "ConnectRH analyse la demande",
      description: "Notre IA comprend le contexte et identifie précisément le type de question posée.",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      hoverBorder: "hover:border-blue-400"
    },
    {
      icon: MagnifyingGlassIcon,
      title: "Consultation de la base RH",
      description: "ConnectRH consulte votre base d'informations RH pour trouver la réponse appropriée.",
      gradient: "from-orange-500 to-red-500",
      bgGradient: "from-orange-50 to-red-50",
      borderColor: "border-orange-200",
      hoverBorder: "hover:border-orange-400"
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Réponse claire et immédiate",
      description: "Votre collaborateur reçoit une réponse précise, claire et adaptée à votre entreprise.",
      gradient: "from-green-500 to-emerald-500",
      bgGradient: "from-green-50 to-emerald-50",
      borderColor: "border-green-200",
      hoverBorder: "hover:border-green-400"
    }
  ];

  return (
    <section id="how-it-works" className={`py-16 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 relative overflow-hidden smooth-scroll-section ${isMobile ? 'mobile-simple' : ''}`}>
      {/* Éléments décoratifs animés - désactivés sur mobile */}
      {!isMobile && (
        <>
          {/* Cercles décoratifs supprimés */}
        </>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
          className="text-center"
        >
          {/* Badge section */}
          <motion.div
            variants={scaleIn}
            whileHover={isMobile ? {} : { scale: 1.05 }}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-full px-6 py-3 mb-8 shadow-lg backdrop-blur-sm"
          >
            {!isMobile ? (
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center"
              >
                <CogIcon className="w-4 h-4 text-white" />
              </motion.div>
            ) : (
              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <CogIcon className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Comment ça marche
            </span>
          </motion.div>

          {/* Titre principal */}
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-relaxed py-4"
            style={{ lineHeight: '1.3' }}
          >
            Un processus simple
            <span className="block bg-gradient-to-r from-blue-600 to-violet-600 gradient-text-safe">
              et efficace
            </span>
          </motion.h2>

          {/* Description */}
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-16"
          >
            ConnectRH simplifie l'accès à l'information RH grâce à un processus en 4 étapes claires et intuitives.
          </motion.p>

          {/* Processus étapes */}
          <motion.div
            variants={staggerContainer}
            className="max-w-6xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <motion.div
                    variants={scaleIn}
                    whileHover={isMobile ? {} : { 
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                    className="relative h-full"
                  >
                    {/* Flèche de connexion animée (desktop uniquement) */}
                    {index < steps.length - 1 && !isMobile && (
                      <motion.div 
                        variants={slideInLeft}
                        className="hidden lg:block absolute top-1/2 -right-3 z-10 transform -translate-y-1/2"
                      >
                        <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-gray-200">
                          <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                        </div>
                      </motion.div>
                    )}

                    <div className={`
                      bg-gradient-to-br ${step.bgGradient} rounded-3xl p-8 text-center border-2 ${step.borderColor} ${step.hoverBorder}
                      transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 h-full flex flex-col
                      backdrop-blur-sm
                    `}>
                      {/* Numéro de l'étape avec animation */}
                      <motion.div
                        variants={scaleIn}
                        whileHover={isMobile ? {} : { rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className={`
                          w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl mb-6 mx-auto
                          bg-gradient-to-r ${step.gradient} shadow-lg
                        `}
                      >
                        {index + 1}
                      </motion.div>

                      {/* Icône avec animation */}
                      {!isMobile ? (
                        <motion.div
                          variants={scaleIn}
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.3 }}
                          className={`
                            w-20 h-20 rounded-3xl flex items-center justify-center mb-6 mx-auto
                            bg-gradient-to-r ${step.gradient} shadow-xl
                          `}
                        >
                          <step.icon className="w-10 h-10 text-white" />
                        </motion.div>
                      ) : (
                        <div className={`
                          w-20 h-20 rounded-3xl flex items-center justify-center mb-6 mx-auto
                          bg-gradient-to-r ${step.gradient} shadow-xl
                        `}>
                          <step.icon className="w-10 h-10 text-white" />
                        </div>
                      )}

                      {/* Contenu */}
                      <div className="flex-1 flex flex-col">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex-grow">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Section "Si ConnectRH ne peut pas répondre" */}
          <motion.div
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            className="mt-20 bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-3xl p-8 max-w-4xl mx-auto border border-gray-200 shadow-lg"
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-gray-500 to-blue-500 rounded-xl flex items-center justify-center">
                <UserGroupIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Et si ConnectRH ne peut pas répondre ?
              </h3>
            </div>
            <p className="text-gray-600 leading-relaxed text-lg">
              Pas de problème ! ConnectRH redirige automatiquement votre collaborateur vers le service RH approprié, 
              en transmettant le contexte de la question pour une prise en charge optimale.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
} 